import { useMemo, useState, type FormEvent } from 'react';
import { Link } from 'react-router-dom';
import {
  Alert,
  V2FormField,
  V2Button,
  type V2ButtonVariant,
  V2FormActions,
} from '@sakhtar/design-system/react';
import { apiPost, ApiError } from '../../lib/apiClient.js';
import { useApiGet } from '../../lib/useApiGet.js';
import { tenantBasePath } from '../../lib/tenant.js';
import { JalaliDateInput } from '../../lib/date/JalaliDateInput.js';
import {
  TASK_PRIORITY_OPTIONS,
  TASK_RELATED_TYPE_OPTIONS,
  type AssignableMembersResponse,
  type ProjectListResponse,
  type ProjectTask,
  type TaskRelatedType,
} from '../../lib/types/projects.js';

type TaskScope = 'project' | 'standalone';
type AssigneeMode = 'self' | 'colleague';

/** Projects that can still take new work — closed ones are not offered. */
const CLOSED_PROJECT_LABELS = new Set(['تکمیل‌شده', 'لغو شده']);

function ChoiceButton({
  selected,
  onClick,
  children,
  disabled,
}: {
  selected: boolean;
  onClick: () => void;
  children: string;
  disabled?: boolean;
}) {
  const variant: V2ButtonVariant = selected ? 'primary' : 'secondary';
  return (
    <V2Button
      type="button"
      variant={variant}
      aria-pressed={selected}
      disabled={disabled}
      onClick={onClick}
      style={{ flex: 1, minHeight: 44 }}
    >
      {children}
    </V2Button>
  );
}

/**
 * Quick "new task" form — shared by the desktop topbar's افزودن سریع
 * popover, the mobile bottom-nav افزودن page, and the Projects › کارها page.
 *
 * The first question is whether the task belongs to a project:
 *  - داخل پروژه → pick a project; the assignee may only be someone on that
 *    project's team (GET .../assignable-members?projectRef=…, enforced again
 *    by the API on create);
 *  - بدون پروژه → a standalone task; the assignee may be any active
 *    colleague (GET .../assignable-members).
 * Then: to me or to a colleague, a title, and an optional deadline
 * (business date, Jalali input → ISO via JalaliDateInput).
 */
export function QuickTaskForm({
  slug,
  compact = false,
  showPriority = false,
  onCreated,
  onCancel,
}: {
  slug: string;
  /** Narrow layout for the topbar popover. */
  compact?: boolean;
  /** Also ask for priority (the full Tasks page); quick-add keeps the default. */
  showPriority?: boolean;
  onCreated?: (task: ProjectTask) => void;
  onCancel?: () => void;
}) {
  const base = tenantBasePath(slug);
  const [scope, setScope] = useState<TaskScope | null>(null);
  const [projectRef, setProjectRef] = useState('');
  const [assigneeMode, setAssigneeMode] = useState<AssigneeMode>('self');
  const [colleagueRef, setColleagueRef] = useState('');
  const [title, setTitle] = useState('');
  const [dueDate, setDueDate] = useState('');
  const [priorityLabel, setPriorityLabel] = useState('medium');
  const [relatedType, setRelatedType] = useState<TaskRelatedType | ''>('');
  const [relatedRef, setRelatedRef] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [created, setCreated] = useState<ProjectTask | null>(null);

  const projects = useApiGet<ProjectListResponse>(
    scope === 'project' ? `${base}/api/v1/projects?limit=100` : null,
    [base, scope],
  );
  const membersPath =
    scope === 'standalone'
      ? `${base}/api/v1/projects/assignable-members`
      : scope === 'project' && projectRef
        ? `${base}/api/v1/projects/assignable-members?projectRef=${encodeURIComponent(projectRef)}`
        : null;
  const members = useApiGet<AssignableMembersResponse>(membersPath, [membersPath]);

  const openProjects = useMemo(
    () =>
      projects.status === 'ready'
        ? projects.data.projects.filter((p) => !CLOSED_PROJECT_LABELS.has(p.statusLabel))
        : [],
    [projects],
  );
  const self = members.status === 'ready' ? members.data.members.find((m) => m.isSelf) : undefined;
  const colleagues =
    members.status === 'ready' ? members.data.members.filter((m) => !m.isSelf) : [];
  const membersReady = members.status === 'ready';
  const membersDenied = members.status === 'error' && members.error.status === 403;

  function chooseScope(next: TaskScope) {
    setScope(next);
    setProjectRef('');
    setColleagueRef('');
    setAssigneeMode('self');
    setError(null);
  }

  function chooseProject(next: string) {
    setProjectRef(next);
    setColleagueRef('');
    setError(null);
  }

  function reset() {
    setCreated(null);
    setTitle('');
    setDueDate('');
    setColleagueRef('');
    setRelatedType('');
    setRelatedRef('');
    setError(null);
  }

  async function onSubmit(event: FormEvent) {
    event.preventDefault();
    setError(null);
    if (scope === null) {
      setError('ابتدا مشخص کنید کار داخل پروژه است یا نه.');
      return;
    }
    if (scope === 'project' && !projectRef) {
      setError('پروژه را انتخاب کنید.');
      return;
    }
    if (!title.trim()) {
      setError('عنوان کار الزامی است.');
      return;
    }
    const assigneeMemberRef = assigneeMode === 'self' ? self?.memberRef : colleagueRef;
    if (!assigneeMemberRef) {
      setError(
        assigneeMode === 'self'
          ? 'شما عضو تیم این پروژه نیستید؛ کار را به یکی از اعضای تیم بدهید.'
          : 'همکار را انتخاب کنید.',
      );
      return;
    }

    setSubmitting(true);
    try {
      const task = await apiPost<ProjectTask>(`${base}/api/v1/projects/tasks`, {
        ...(scope === 'project' ? { projectRef } : {}),
        title: title.trim(),
        ...(dueDate ? { dueDate } : {}),
        ...(showPriority ? { priorityLabel } : {}),
        ...(relatedType ? { relatedType, relatedRef: relatedRef.trim() || undefined } : {}),
        assigneeMemberRef,
      });
      setCreated(task);
      onCreated?.(task);
    } catch (caught) {
      if (caught instanceof ApiError && caught.status === 403) {
        setError('اجازهٔ ساخت کار برای شما فعال نیست.');
      } else if (caught instanceof ApiError && caught.status === 422) {
        setError('مسئول انتخاب‌شده برای این کار مجاز نیست. فهرست را دوباره بررسی کنید.');
      } else {
        setError('ثبت کار ناموفق بود.');
      }
    } finally {
      setSubmitting(false);
    }
  }

  const gap = compact ? 12 : 16;

  if (created) {
    return (
      <div style={{ display: 'flex', flexDirection: 'column', gap }}>
        <Alert tone="success" title="کار ثبت شد">
          «{created.title}»{created.assignee ? ` برای ${created.assignee.displayLabel}` : ''} ثبت
          شد.
        </Alert>
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
          <V2Button type="button" variant="primary" onClick={reset}>
            کار دیگر
          </V2Button>
          <Link
            to={`${base}/projects/tasks`}
            className="sk-btn sk-btn--secondary"
            style={{ textDecoration: 'none' }}
          >
            مشاهدهٔ کارها
          </Link>
          {onCancel ? (
            <V2Button type="button" variant="ghost" onClick={onCancel}>
              بستن
            </V2Button>
          ) : null}
        </div>
      </div>
    );
  }

  return (
    <form
      onSubmit={onSubmit}
      style={{ display: 'flex', flexDirection: 'column', gap }}
      aria-label="کار جدید"
    >
      <div role="group" aria-label="این کار داخل پروژه است؟">
        <div style={{ fontSize: 13, fontWeight: 600, marginBottom: 8 }}>
          این کار داخل پروژه است؟
        </div>
        <div style={{ display: 'flex', gap: 8 }}>
          <ChoiceButton selected={scope === 'project'} onClick={() => chooseScope('project')}>
            داخل پروژه
          </ChoiceButton>
          <ChoiceButton selected={scope === 'standalone'} onClick={() => chooseScope('standalone')}>
            بدون پروژه
          </ChoiceButton>
        </div>
      </div>

      {scope === 'project' ? (
        <V2FormField width="full" label="پروژه" required>
          <select
            className="sk-select"
            value={projectRef}
            onChange={(e) => chooseProject(e.target.value)}
            disabled={projects.status !== 'ready'}
          >
            <option value="">
              {projects.status === 'loading' ? 'در حال بارگذاری…' : 'انتخاب پروژه…'}
            </option>
            {openProjects.map((p) => (
              <option key={p.ref} value={p.ref}>
                {p.name}
              </option>
            ))}
          </select>
        </V2FormField>
      ) : null}
      {scope === 'project' && projects.status === 'ready' && openProjects.length === 0 ? (
        <Alert tone="info" title="پروژهٔ بازی وجود ندارد">
          کار را «بدون پروژه» ثبت کنید یا ابتدا پروژه‌ای بسازید.
        </Alert>
      ) : null}

      {membersDenied ? (
        <Alert tone="warning" title="دسترسی ندارید">
          اجازهٔ ساخت کار برای شما فعال نیست.
        </Alert>
      ) : null}

      {scope !== null && (scope === 'standalone' || projectRef) && !membersDenied ? (
        <>
          <div role="group" aria-label="مسئول کار">
            <div style={{ fontSize: 13, fontWeight: 600, marginBottom: 8 }}>برای چه کسی؟</div>
            <div style={{ display: 'flex', gap: 8 }}>
              <ChoiceButton
                selected={assigneeMode === 'self'}
                onClick={() => setAssigneeMode('self')}
                disabled={membersReady && !self}
              >
                خودم
              </ChoiceButton>
              <ChoiceButton
                selected={assigneeMode === 'colleague'}
                onClick={() => setAssigneeMode('colleague')}
              >
                همکار
              </ChoiceButton>
            </div>
            {membersReady && !self && scope === 'project' ? (
              <div style={{ fontSize: 12, color: 'var(--sk-fg-muted)', marginTop: 6 }}>
                شما عضو تیم این پروژه نیستید.
              </div>
            ) : null}
          </div>

          {assigneeMode === 'colleague' ? (
            <V2FormField
              width="full"
              label="همکار"
              required
              hint={scope === 'project' ? 'فقط اعضای تیم همین پروژه' : undefined}
            >
              <select
                className="sk-select"
                value={colleagueRef}
                onChange={(e) => setColleagueRef(e.target.value)}
                disabled={!membersReady}
              >
                <option value="">
                  {members.status === 'loading' ? 'در حال بارگذاری…' : 'انتخاب همکار…'}
                </option>
                {colleagues.map((m) => (
                  <option key={m.memberRef} value={m.memberRef}>
                    {m.displayLabel}
                  </option>
                ))}
              </select>
            </V2FormField>
          ) : null}
          {assigneeMode === 'colleague' && membersReady && colleagues.length === 0 ? (
            <Alert tone="info" title="همکاری برای انتخاب نیست">
              {scope === 'project' ? (
                <>
                  هنوز همکاری به تیم این پروژه اضافه نشده.{' '}
                  <Link to={`${base}/projects/list`}>مدیریت تیم پروژه</Link>
                </>
              ) : (
                'همکار فعالی در سازمان پیدا نشد.'
              )}
            </Alert>
          ) : null}

          <V2FormField width="full" label="عنوان کار" required>
            <input
              className="sk-input"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder="مثلاً پیگیری پیش‌فاکتور مشتری"
              required
            />
          </V2FormField>

          <V2FormField width="full" label="ددلاین" hint="اختیاری">
            <JalaliDateInput value={dueDate} onChange={setDueDate} />
          </V2FormField>

          {showPriority ? (
            <V2FormField width="full" label="اولویت">
              <select
                className="sk-select"
                value={priorityLabel}
                onChange={(e) => setPriorityLabel(e.target.value)}
              >
                {TASK_PRIORITY_OPTIONS.map((opt) => (
                  <option key={opt.value} value={opt.value}>
                    {opt.label}
                  </option>
                ))}
              </select>
            </V2FormField>
          ) : null}

          <div
            style={{
              display: 'grid',
              gridTemplateColumns: compact ? '1fr' : '1fr 1fr',
              gap: 12,
              background: 'var(--sk-bg-sunken)',
              padding: 12,
              borderRadius: 8,
              border: '1px dashed var(--sk-divider)',
            }}
          >
            <V2FormField width="full" label="مرتبط با (اختیاری)" hint="اتصال به مشتری یا سند">
              <select
                className="sk-select"
                value={relatedType}
                onChange={(e) => {
                  setRelatedType(e.target.value as TaskRelatedType | '');
                  if (!e.target.value) setRelatedRef('');
                }}
              >
                <option value="">بدون ارتباط (عمومی)</option>
                {TASK_RELATED_TYPE_OPTIONS.map((opt) => (
                  <option key={opt.value} value={opt.value}>
                    {opt.label}
                  </option>
                ))}
              </select>
            </V2FormField>

            {relatedType ? (
              <V2FormField width="full" label="شناسه یا کد مرجع" hint="نام مشتری یا شماره سند">
                <input
                  className="sk-input"
                  value={relatedRef}
                  onChange={(e) => setRelatedRef(e.target.value)}
                  placeholder={
                    relatedType === 'party'
                      ? 'نام یا کد مشتری'
                      : relatedType === 'sales_order'
                        ? 'مثلاً SO-0001'
                        : relatedType === 'purchase_order'
                          ? 'مثلاً PO-0001'
                          : relatedType === 'invoice'
                            ? 'مثلاً INV-0001'
                            : 'کد مرجع فرصت'
                  }
                />
              </V2FormField>
            ) : null}
          </div>
        </>
      ) : null}

      {error ? (
        <Alert tone="danger" title="خطا">
          {error}
        </Alert>
      ) : null}

      <V2FormActions>
        {onCancel ? (
          <V2Button type="button" variant="ghost" onClick={onCancel}>
            انصراف
          </V2Button>
        ) : null}
        <V2Button
          type="submit"
          variant="primary"
          icon="plus"
          disabled={submitting || scope === null || membersDenied}
        >
          {submitting ? 'در حال ثبت…' : 'ثبت کار'}
        </V2Button>
      </V2FormActions>
    </form>
  );
}
